import { isDefined } from "@clipboard-health/util-ts";
import { type ShiftChargeRateInfo } from "@src/appV2/FacilityDashboard/VariableCharge/hooks/useShiftChargeRates";

/**
 * Converts minor units (cents) to major units (dollars)
 */
export function minorToMajor(minorUnits: number): number {
  return minorUnits / 100;
}

/**
 * Converts major units (dollars) to minor units (cents)
 */
export function majorToMinor(majorUnits: number): number {
  return Math.round(majorUnits * 100);
}

/**
 * Validates an input string as a currency value: empty, a lone decimal point, or a number with up
 * to two decimal places. Used by the charge-rate and fixed-bonus inputs to reject bad keystrokes.
 */
export function isValidCurrencyInput(value: string): boolean {
  if (value === "" || value === ".") {
    return true;
  }
  const parsedNumber = Number(value);
  return !Number.isNaN(parsedNumber) && /^\d*\.?\d{0,2}$/.exec(value) !== null;
}

/**
 * Gets the initial charge rate to display in the dialog.
 * Matches the logic from the calendar view's getInitialChargeRate function.
 *
 * Priority order:
 * 1. Manual charge rate (if set and different from default)
 * 2. Wait and Save maximum auction rate (if Wait and Save is active)
 * 3. Current shift charge rate
 */
export function getInitialChargeRate(info: ShiftChargeRateInfo): number {
  // Manual charge rates always take precedence over wait and save auctions
  if (isDefined(info.manualRateInMinorUnits)) {
    return info.manualRateInMinorUnits;
  }

  // If the shift has wait and save, the rate we're editing is the maximum auction rate
  // rather than the current rate of the shift
  if (isDefined(info.waitAndSaveAuction)) {
    return info.waitAndSaveAuction.maximumChargeInMinorUnits;
  }

  return info.currentRateInMinorUnits;
}
